import {
  COHORT_METHODS,
  COHORT_METHOD_LABEL,
  COHORT_N_LIMITS,
  CohortInputRule,
  CohortMethod,
  defaultRuleFor,
  normalizeCohortRule
} from './cohort-forecast';
import { describeMixRule, forecastMix, mixRuleTag } from './mix-forecast';

export type SourceMethod = CohortMethod | 'spend' | 'direct';

export interface SourceRule {
  method: SourceMethod;
  n?: number;
  value?: number;
}

export type SourceRuleMap = Record<string, SourceRule>;

export interface SourceSeries {
  key: string;
  name: string;
  ad: Array<number | null>;
  sess: Array<number | null>;
  cps: Array<number | null>;
}

export interface SourceResolved {
  key: string;
  name: string;
  ad: Array<number | null>;
  sessions: Array<number | null>;
  cps: Array<number | null>;
  sessionRule: SourceRule;
  cpsRule: SourceRule;
  spendDriven: boolean;
  directSessions: boolean;
  cpsMissing: boolean[];
  cpsTyped: boolean[];
  peak: boolean[];
}

export interface SourceForecastInput {
  months: number[];
  cut: number;
  sources: SourceSeries[];
  sessionRuleFor: (src: SourceSeries) => SourceRule;
  cpsRuleFor: (src: SourceSeries) => SourceRule;
}

export const SESSION_METHODS: SourceMethod[] = (COHORT_METHODS as SourceMethod[]).concat(['spend', 'direct']);
export const CPS_METHODS: SourceMethod[] = (COHORT_METHODS as SourceMethod[]).concat(['direct']);

export const SOURCE_METHOD_LABEL: Record<SourceMethod, string> = Object.assign(
  { spend: 'Spend driven', direct: 'Direct input' },
  COHORT_METHOD_LABEL
) as Record<SourceMethod, string>;

export const DIRECT_RULE: SourceRule = { method: 'direct' };
export const SPEND_RULE: SourceRule = { method: 'spend' };
export const DEFAULT_CPS_RULE: SourceRule = { method: 'avg', n: 3 };
export const DEFAULT_SEEDED_SESSION_RULE: SourceRule = { method: 'seasonal', n: 3 };

export const SPEND_DRIVEN_SOURCES = ['paid', 'affiliates'];

export const CPS_MISSING_NOTE = 'Cost per session must be above zero before sessions can be worked out.';
export const MANUAL_SOURCE_NOTE = 'Manual source · saved in the app, not Snowflake. Every month is editable and accepts + or − values to adjust actuals.';
export const MANUAL_ACTUAL_NOTE = 'Manual adjustment for this actual month. Type a + or − value; it adds to the Snowflake actuals in the totals.';
export const PEAK_NOTE = 'Spend above historical peak at the same or lower cost per session. Costs usually rise at higher spend.';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function isSessionMethod(v: unknown): v is SourceMethod {
  return typeof v === 'string' && (SESSION_METHODS as string[]).indexOf(v) >= 0;
}

export function normalizeSourceRule(raw: unknown, methods: SourceMethod[]): SourceRule | null {
  if (!raw || typeof raw !== 'object') return null;
  const method = String((raw as { method?: unknown }).method || '').trim().toLowerCase();
  if (methods.indexOf(method as SourceMethod) < 0) return null;
  if (method === 'spend' || method === 'direct') return { method: method as SourceMethod };
  return normalizeCohortRule(raw) as SourceRule | null;
}

function sanitize(raw: unknown, methods: SourceMethod[]): SourceRuleMap {
  const out: SourceRuleMap = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw as Record<string, unknown>).forEach(key => {
    if (!/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/.test(key)) return;
    const rule = normalizeSourceRule((raw as Record<string, unknown>)[key], methods);
    if (rule) out[key] = rule;
  });
  return out;
}

export const sanitizeSessionInput = (raw: unknown): SourceRuleMap => sanitize(raw, SESSION_METHODS);
export const sanitizeCpsInput = (raw: unknown): SourceRuleMap => sanitize(raw, CPS_METHODS);

export const SOURCE_METRIC_KEY: Record<string, string> = { ad: 'ad_spend', sess: 'sessions', cps: 'cost_per_session' };

export const sourceDriverKey = (sourceKey: string, metric: string): string => sourceKey + '.' + (SOURCE_METRIC_KEY[metric] || metric);

export const sourceKeyLeaf = (sourceKey: string): string => String(sourceKey || '').split('.').pop() || '';

export function seededSessionRule(sourceKey: string): SourceRule {
  return SPEND_DRIVEN_SOURCES.indexOf(sourceKeyLeaf(sourceKey)) >= 0 ? SPEND_RULE : DEFAULT_SEEDED_SESSION_RULE;
}

export function resolveSessionRule(map: SourceRuleMap | null | undefined, sourceKey: string): SourceRule {
  return (map ? map[sourceKey] : null) || DIRECT_RULE;
}

export function resolveCpsRule(map: SourceRuleMap | null | undefined, sourceKey: string): SourceRule {
  return (map ? map[sourceKey] : null) || DEFAULT_CPS_RULE;
}

export function describeSourceRule(rule: SourceRule): string {
  if (rule.method === 'spend') return 'Spend driven · ad spend ÷ cost per session';
  if (rule.method === 'direct') return 'Direct input · typed every forecast month';
  return describeMixRule(rule as CohortInputRule);
}

export function sourceRuleTag(rule: SourceRule): string {
  if (rule.method === 'spend') return 'SPEND';
  if (rule.method === 'direct') return 'DIRECT';
  return mixRuleTag(rule as CohortInputRule);
}

export function defaultSourceRuleFor(method: SourceMethod, current?: SourceRule | null): SourceRule {
  if (method === 'spend' || method === 'direct') return { method };
  return defaultRuleFor(method as CohortMethod, current as CohortInputRule) as SourceRule;
}

export function sourceRuleLimits(rule: SourceRule): [number, number] | null {
  if (rule.method === 'spend' || rule.method === 'direct' || rule.method === 'fixed') return null;
  return COHORT_N_LIMITS[rule.method as Exclude<CohortMethod, 'fixed'>];
}

const ratio = (num: number | null, den: number | null): number | null =>
  isNum(num) && isNum(den) && num > 0 && den > 0 ? num / den : null;

function historyOf(months: number[], cut: number, values: Array<number | null>): Record<string, number> {
  const out: Record<string, number> = {};
  for (let t = 0; t <= Math.min(cut, months.length - 1); t++) {
    const v = values[t];
    if (isNum(v)) out[String(months[t])] = v;
  }
  return out;
}

function runRules(
  months: number[],
  cut: number,
  rows: Array<{ name: string; values: Array<number | null>; rule: SourceRule }>,
  weightsOf: (index: number) => Array<number | null>
): Array<Array<number | null>> {
  return rows.map((row, i) =>
    forecastMix({
      months,
      cut,
      cats: [{ name: row.name, base: null, history: historyOf(months, cut, row.values) }],
      ruleFor: () => row.rule as CohortInputRule,
      weights: weightsOf(i)
    }).values[0]
  );
}

export function forecastSources(input: SourceForecastInput): SourceResolved[] {
  const { months, cut, sources, sessionRuleFor, cpsRuleFor } = input;
  const n = months.length;
  const last = Math.min(cut, n - 1);

  return sources.map(src => {
    const sessionRule = sessionRuleFor(src) || DIRECT_RULE;
    const cpsRule = cpsRuleFor(src) || DEFAULT_CPS_RULE;
    const spendDriven = sessionRule.method === 'spend';
    const directSessions = sessionRule.method === 'direct';

    const ad = months.map((_, t) => (isNum(src.ad[t]) ? src.ad[t] : null));
    const sessions: Array<number | null> = new Array(n).fill(null);
    const cps: Array<number | null> = new Array(n).fill(null);

    for (let t = 0; t <= last; t++) {
      sessions[t] = isNum(src.sess[t]) ? src.sess[t] : null;
      cps[t] = ratio(ad[t], sessions[t]);
    }

    const cpsTyped = months.map((_, t) => spendDriven && t > last && isNum(src.cps[t]));

    if (spendDriven) {
      const planned = cpsRule.method === 'direct'
        ? null
        : runRules(months, cut, [{ name: src.name, values: cps, rule: cpsRule }], () => sessions)[0];
      for (let t = last + 1; t < n; t++) {
        cps[t] = cpsTyped[t] ? (src.cps[t] as number) : planned ? planned[t] : null;
        const rate = cps[t];
        sessions[t] = isNum(ad[t]) && isNum(rate) && rate > 0 ? Math.round((ad[t] as number) / rate) : null;
      }
    } else if (directSessions) {
      for (let t = last + 1; t < n; t++) sessions[t] = isNum(src.sess[t]) ? src.sess[t] : null;
    } else {
      const planned = runRules(months, cut, [{ name: src.name, values: sessions, rule: sessionRule }], () => ad)[0];
      for (let t = last + 1; t < n; t++) sessions[t] = isNum(planned[t]) ? Math.round(planned[t] as number) : null;
    }

    const cpsMissing = months.map((_, t) => spendDriven && t > last && isNum(ad[t]) && !(isNum(cps[t]) && (cps[t] as number) > 0));

    let peakAd: number | null = null;
    for (let t = 0; t <= last; t++) if (isNum(ad[t])) peakAd = peakAd === null ? (ad[t] as number) : Math.max(peakAd, ad[t] as number);
    let sum = 0, used = 0;
    for (let t = last; t >= 0 && used < 3; t--) {
      if (!isNum(cps[t])) continue;
      sum += cps[t] as number;
      used++;
    }
    const trailing = used ? sum / used : null;
    const peak = months.map((_, t) =>
      t > last && peakAd !== null && trailing !== null && isNum(ad[t]) && (ad[t] as number) > peakAd && isNum(cps[t]) && (cps[t] as number) <= trailing
    );

    return { key: src.key, name: src.name, ad, sessions, cps, sessionRule, cpsRule, spendDriven, directSessions, cpsMissing, cpsTyped, peak };
  });
}
